import { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useMutation, useQuery } from '@tanstack/react-query';

import {
  createWorkOrderRepairCorrection,
  fetchWorkOrderRepairCorrections,
} from '#/api/momMgmt/workOrderRepair';
import { Button } from '#/modules/commonComponents/button';
import { Checkbox } from '#/modules/commonComponents/checkbox';
import { AppDialog } from '#/modules/commonComponents/dialog';
import { DialogFooter } from '#/modules/commonComponents/dialog/DialogPrimitives';
import FloatingTextarea from '#/modules/commonComponents/form/FloatingTextarea';
import {
  getWorkOrderRepairErrorMessage,
  listCorrectableRepairMaterials,
} from '#/modules/momMgmt/workOrder/helper/workOrderRepair';
import { formatDisplayQty } from '#/utils/formatNumber';
import { toast } from '#/utils/toast';

function newIdempotencyKey() {
  return globalThis.crypto?.randomUUID?.() ?? `wor-${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

/**
 * 已結維修單更正：選用料全額沖回（每筆只能一次），送出即過帳、不可撤回。
 * 同一個視窗內重送沿用同一把 Idempotency-Key，避免重複沖回。
 */
const WorkOrderRepairCorrectionDialog = ({ open, onOpenChange, repair, onPosted }) => {
  const [reason, setReason] = useState('');
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  // 呼叫端每次開啟都重新掛載（key），所以一個視窗＝一把 key
  const [idempotencyKey] = useState(newIdempotencyKey);

  const correctionsQuery = useQuery({
    queryKey: ['erp', 'workOrderRepair', 'corrections', repair?.id],
    queryFn: () => fetchWorkOrderRepairCorrections(repair.id),
    enabled: open && Boolean(repair?.id),
  });
  const correctable = useMemo(
    () => listCorrectableRepairMaterials(repair, correctionsQuery.data?.data),
    [repair, correctionsQuery.data?.data],
  );
  const hasMaterials = (repair?.materials ?? []).length > 0;

  const mutation = useMutation({
    mutationFn: () => createWorkOrderRepairCorrection(
      repair.id,
      { reason, materialIds: [...selectedIds] },
      { idempotencyKey },
    ),
    onSuccess: () => {
      toast.success('已建立更正並沖回所選用料');
      onPosted?.();
      onOpenChange(false);
    },
    onError: (error) => toast.error(getWorkOrderRepairErrorMessage(error, '更正失敗')),
  });

  function toggle(id, checked) {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (checked) {
        next.add(id);
      } else {
        next.delete(id);
      }
      return next;
    });
  }

  const needsSelection = hasMaterials && selectedIds.size === 0;
  const canSubmit = reason.trim() !== '' && !needsSelection && !mutation.isPending;

  const footer = (
    <DialogFooter>
      <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>取消</Button>
      <Button type="button" disabled={!canSubmit} onClick={() => mutation.mutate()}>
        {mutation.isPending ? '過帳中…' : '確認更正'}
      </Button>
    </DialogFooter>
  );

  return (
    <AppDialog
      open={open}
      onOpenChange={onOpenChange}
      title={`更正維修單 ${repair?.repairNo ?? ''}`}
      description="送出後立即過帳：所選用料的扣帳會全額沖回入庫，且無法撤回。"
      footer={footer}
    >
      <div className="flex flex-col gap-3">
        <FloatingTextarea
          id="correctionReason"
          label="更正理由"
          value={reason}
          onChange={(event) => setReason(event.target.value)}
          rows={2}
        />
        {!hasMaterials ? (
          <p className="text-sm text-muted-foreground">此維修單沒有用料，更正只會留下稽核紀錄。</p>
        ) : null}
        {hasMaterials && correctable.length === 0 && !correctionsQuery.isLoading ? (
          <p className="text-sm text-muted-foreground">所有用料都已沖回，沒有可更正的項目。</p>
        ) : null}
        {correctable.length > 0 ? (
          <ul className="m-0 flex list-none flex-col gap-2 p-0">
            {correctable.map((material, index) => (
              <li key={material.id} className="flex items-center gap-2 text-sm">
                <Checkbox
                  checked={selectedIds.has(material.id)}
                  onCheckedChange={(value) => toggle(material.id, value === true)}
                  aria-label={`沖回用料 ${index + 1}`}
                />
                <span className="text-gray-800">
                  用料 #{index + 1}：{formatDisplayQty(material.qty)} {material.unitNameSnapshot ?? ''}
                  （基本單位 {formatDisplayQty(material.baseQty)} {material.baseUnitNameSnapshot ?? ''}）
                </span>
              </li>
            ))}
          </ul>
        ) : null}
      </div>
    </AppDialog>
  );
};

WorkOrderRepairCorrectionDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onOpenChange: PropTypes.func.isRequired,
  repair: PropTypes.object,
  onPosted: PropTypes.func,
};

export default WorkOrderRepairCorrectionDialog;
